/**
 * Turning what a person typed into the integers this system stores.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY QUANTITY IN STEELBILL IS AN INTEGER (CLAUDE.md, §4):
 *
 *   money      integer PAISE          `67.50`  → 6750
 *   quantity   integer MILLI-UNITS    `1.000`  → 1000
 *   weight     integer GRAMS          `12.5`kg → 12500
 *   length     integer MILLIMETRES    `2.4`m   → 2400
 *   GST rate   BASIS POINTS           `18`%    → 1800,  `2.5`% → 250
 *
 * All five are the same operation — a decimal string scaled by a power of ten —
 * and all five must be done WITHOUT floating point. `Math.round(2.4 * 1000)` is
 * 2400 today and something else for a value nobody tested; `0.1 * 3` is famously
 * not `0.3`. On a tax invoice that is not a rounding curiosity, it is a wrong
 * number on a legal document.
 *
 * So the string is SPLIT on the decimal point and each half read as an integer,
 * exactly as `parseRupeesToPaise` does for money (lib/money.ts). This is the
 * generalisation of that function; money keeps its own name because that is what
 * a screen author reaches for, and because it accepts `₹` and grouping commas
 * that a weight field must not.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * `"2.4"`, 3 → `2400`. Returns `null` for anything unreadable — never `NaN`,
 * never `0`, because a silent zero on a quantity field means a line billed for
 * nothing.
 *
 * Accepts a leading `-`, surrounding space and grouping commas. REJECTS more
 * decimal places than the scale can hold rather than truncating: `1.9999` kg is
 * not 1999 grams, and quietly dropping the last digit is how a rate gets typed
 * once and billed differently.
 */
export function parseScaled(input: string, decimals: number): number | null {
  const cleaned = input.replace(/[\s,]/g, "");
  if (cleaned === "") return null;

  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(cleaned);
  if (match === null) return null;

  const [, sign = "", whole = "", fraction = ""] = match;
  if (whole === "" && fraction === "") return null;
  if (fraction.length > decimals) return null;

  const scale = 10 ** decimals;
  const wholeValue = whole === "" ? 0 : Number.parseInt(whole, 10);
  const fractionValue = fraction === "" ? 0 : Number.parseInt(fraction.padEnd(decimals, "0"), 10);

  if (!Number.isSafeInteger(wholeValue) || !Number.isSafeInteger(fractionValue)) return null;

  const total = wholeValue * scale + fractionValue;
  if (!Number.isSafeInteger(total)) return null;
  return sign === "-" ? -total : total;
}

/** `2400`, 3 → `"2.400"`. The editable form of a scaled integer. */
export function formatScaled(value: number, decimals: number): string {
  const negative = value < 0;
  const absolute = Math.abs(Math.trunc(value));
  const scale = 10 ** decimals;
  const whole = Math.floor(absolute / scale);
  const fraction = absolute % scale;
  return `${negative ? "-" : ""}${String(whole)}.${String(fraction).padStart(decimals, "0")}`;
}

/** `"1.000"` → `1000` milli-units. Quantities carry three decimals (§4). */
export function parseQtyToMilli(input: string): number | null {
  return parseScaled(input, 3);
}

/** `1000` → `"1.000"`. */
export function milliToInputValue(value: number): string {
  return formatScaled(value, 3);
}

/** `"12.5"` kg → `12500` grams. A gram is a milli-kilogram, so the scale is 3. */
export function parseKgToGrams(input: string): number | null {
  return parseScaled(input, 3);
}

/** `"2.4"` m → `2400` mm. A millimetre is a milli-metre, so the scale is 3. */
export function parseMetresToMm(input: string): number | null {
  return parseScaled(input, 3);
}

/**
 * `"18"` → `1800`, `"2.5"` → `250`. A GST rate in basis points.
 *
 * Two decimals because that is what basis points ARE — 1800 bp is 18.00%. A rate
 * with more precision than that cannot be stored, and rejecting it is better
 * than storing a rounded version of a number the CA supplied exactly.
 */
export function parsePercentToBasisPoints(input: string): number | null {
  return parseScaled(input, 2);
}

/** `1800` → `"18"`, `250` → `"2.5"`. Trailing zeros trimmed, as a rate reads. */
export function basisPointsToInputValue(value: number): string {
  return formatScaled(value, 2).replace(/\.?0+$/, "");
}
